import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react"

type RefreshTarget = () => Promise<unknown> | unknown

type ReportingRefreshContextValue = {
  isRefreshing: boolean
  refreshResults: () => Promise<void>
  registerRefreshTarget: (id: string, refresh: RefreshTarget) => () => void
}

const ReportingRefreshContext =
  createContext<ReportingRefreshContextValue | null>(null)

export const ReportingRefreshProvider: React.FC = ({ children }) => {
  const targetsRef = useRef(new Map<string, RefreshTarget>())
  const refreshInFlightRef = useRef(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const registerRefreshTarget = useCallback(
    (id: string, refresh: RefreshTarget) => {
      targetsRef.current.set(id, refresh)

      return () => {
        if (targetsRef.current.get(id) === refresh) {
          targetsRef.current.delete(id)
        }
      }
    },
    []
  )

  const refreshResults = useCallback(async () => {
    if (refreshInFlightRef.current) return

    refreshInFlightRef.current = true
    setIsRefreshing(true)
    try {
      await Promise.all(
        Array.from(targetsRef.current.values()).map(refresh =>
          Promise.resolve(refresh())
        )
      )
    } finally {
      refreshInFlightRef.current = false
      setIsRefreshing(false)
    }
  }, [])

  const value = useMemo(
    () => ({
      isRefreshing,
      refreshResults,
      registerRefreshTarget
    }),
    [isRefreshing, refreshResults, registerRefreshTarget]
  )

  return (
    <ReportingRefreshContext.Provider value={value}>
      {children}
    </ReportingRefreshContext.Provider>
  )
}

export const useReportingRefresh = () => {
  const value = useContext(ReportingRefreshContext)

  if (!value) {
    return {
      isRefreshing: false,
      refreshResults: async () => undefined,
      registerRefreshTarget: () => () => undefined
    }
  }

  return value
}
